import { Box, styled } from '@mui/material';
import { BoxProps } from '@mui/system';
import React from 'react';

const ProgressContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'compact',
})(({ theme, compact }: { theme: any; compact: boolean }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0,
  padding: compact ? 0 : theme.spacing(0.5, 0),
})) as unknown as React.FC<BoxProps & { compact: boolean }>;

interface ChevronStepProps {
  isActive: boolean;
  isCompleted: boolean;
  rounded: boolean;
  height: string;
}

const ChevronStep = styled(Box, {
  shouldForwardProp: (prop) =>
    !['isActive', 'isCompleted', 'rounded', 'height'].includes(prop as string),
})<ChevronStepProps & { rounded: boolean }>(
  ({ theme, isActive, isCompleted, rounded, height }) => {
    const getBackgroundColor = () => {
      if (isActive) return theme.palette.action.active;
      if (isCompleted) return theme.palette.primary.main;
      return theme.palette.action.disabledBackground;
    };

    const getTextColor = () => {
      if (isActive || isCompleted) return theme.palette.primary.contrastText;
      return theme.palette.text.disabled;
    };

    return {
      position: 'relative',
      flex: 1,
      height,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      backgroundColor: getBackgroundColor(),
      color: getTextColor(),
      clipPath:
        'polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%, 16px 50%)',
      marginLeft: '-16px',
      paddingLeft: '24px',
      paddingRight: '28px',
      transition: 'all 0.3s ease',
      marginRight: '2px',
      fontWeight: isActive ? 600 : 500,
      fontSize: '0.75rem',
      '&:first-of-type': {
        clipPath:
          'polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%)',
        marginLeft: 0,
        paddingLeft: theme.spacing(2),
        borderTopLeftRadius: rounded ? '50px' : '0',
        borderBottomLeftRadius: rounded ? '50px' : '0',
      },
      '&:last-of-type': {
        clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 16px 50%)',
        borderTopRightRadius: rounded ? '50px' : '0',
        borderBottomRightRadius: rounded ? '50px' : '0',
        marginRight: 0,
      },
    };
  },
) as unknown as React.FC<ChevronStepProps & BoxProps>;

export interface SteppedProgressProps {
  /** Array of step labels to display */
  steps: string[];
  /** Current step (1-indexed) */
  currentStep: number;
  /** If true, only shows label for current step */
  compact?: boolean;
  /** If true, rounds the corners of the chevron steps */
  rounded?: boolean;
  /** Height of the chevron steps */
  height?: number;
}

/**
 * A chevron-shaped stepped progress indicator component.
 * Displays a horizontal sequence of steps with chevron shapes,
 * highlighting the current step and showing completed steps.
 * @param root0 - Props object
 * @param root0.steps - Array of step labels to display
 * @param root0.currentStep - Current step (1-indexed)
 * @param root0.compact - If true, only shows label for current step
 * @param root0.rounded
 * @param root0.height
 * @returns A React component rendering the stepped progress indicator.
 * @example
 * ```tsx
 * <SteppedProgress
 *   steps={['Select Producer', 'Schedule Details', 'Review']}
 *   currentStep={2}
 *   compact={false}
 * />
 * ```
 */
export function SteppedProgress({
  steps,
  currentStep,
  compact = false,
  rounded = true,
  height = 24,
}: SteppedProgressProps) {
  return (
    <ProgressContainer
      role="progressbar"
      aria-valuenow={currentStep}
      aria-valuemin={1}
      aria-valuemax={steps.length}
      aria-label={`Step ${currentStep} of ${steps.length}: ${steps[currentStep - 1]}`}
      compact={compact}
    >
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isCurrentStep = currentStep === stepNumber;
        const displayLabel = compact && currentStep !== index + 1 ? '' : step;

        return (
          <ChevronStep
            key={stepNumber}
            isActive={isCurrentStep}
            isCompleted={currentStep > stepNumber}
            aria-current={isCurrentStep ? 'step' : undefined}
            rounded={rounded}
            height={`${height}px`}
          >
            {displayLabel}
          </ChevronStep>
        );
      })}
    </ProgressContainer>
  );
}
